import { formatLocalDate, parseDateValue, parseLocalDateKey } from '@/utils/localDateUtils';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';

interface RecentActivity {
  type: 'payment' | 'contract';
  description: string;
  date: string;
  id: number;
}

interface QuickAction {
  title: string;
  description: string;
  path: string;
  icon: ReactNode;
  accent: string;
  permission?: [string, string];
}

const getItems = (payload: any): any[] => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.items) ? data.items : [];
};

const ActionIcon = ({ children }: { children: ReactNode }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-6 w-6">
    {children}
  </svg>
);

const QUICK_ACTIONS: QuickAction[] = [
  {
    title: 'Nuevo Cliente',
    description: 'Registrar propietario o inquilino',
    path: '/clients/new',
    permission: ['clients', 'write'],
    accent: 'bg-sky-50 text-sky-700 group-hover:bg-sky-100',
    icon: <ActionIcon><path d="M15 19a6 6 0 0 0-12 0" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M16 11h6" /></ActionIcon>,
  },
  {
    title: 'Nuevo Inmueble',
    description: 'Agregar una propiedad a la cartera',
    path: '/properties/new',
    permission: ['properties', 'write'],
    accent: 'bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100',
    icon: <ActionIcon><path d="m3 11 9-7 9 7" /><path d="M5 10v10h14V10M9 20v-6h6v6" /></ActionIcon>,
  },
  {
    title: 'Nuevo Contrato',
    description: 'Crear y configurar un alquiler',
    path: '/contracts/new',
    permission: ['contracts', 'write'],
    accent: 'bg-violet-50 text-violet-700 group-hover:bg-violet-100',
    icon: <ActionIcon><path d="M6 3h9l3 3v15H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" /></ActionIcon>,
  },
  {
    title: 'Nuevo Recibo',
    description: 'Emitir un recibo de pago',
    path: '/receipts/new',
    permission: ['receipts', 'write'],
    accent: 'bg-amber-50 text-amber-700 group-hover:bg-amber-100',
    icon: <ActionIcon><path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6M9 12h6" /></ActionIcon>,
  },
  {
    title: 'Recibos Recientes',
    description: 'Consultar los recibos creados por día',
    path: '/receipts/recent',
    permission: ['receipts', 'read'],
    accent: 'bg-cyan-50 text-cyan-700 group-hover:bg-cyan-100',
    icon: <ActionIcon><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18M8 14h3M8 17h5" /></ActionIcon>,
  },
  {
    title: 'Candidatos a Jurídica',
    description: 'Revisar contratos que cumplen los criterios',
    path: '/reports/legal-list?tab=candidates',
    permission: ['reports', 'read'],
    accent: 'bg-rose-50 text-rose-700 group-hover:bg-rose-100',
    icon: <ActionIcon><path d="M12 3v18M6 6h12M7 6l-4 7h8zM17 6l-4 7h8zM8 21h8" /></ActionIcon>,
  },
  {
    title: 'Ver Reportes',
    description: 'Acceder a informes operativos',
    path: '/reports',
    permission: ['reports', 'read'],
    accent: 'bg-indigo-50 text-indigo-700 group-hover:bg-indigo-100',
    icon: <ActionIcon><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></ActionIcon>,
  },
];

const QuickActionCard = ({ action }: { action: QuickAction }) => (
  <Link
    to={action.path}
    className="group flex min-h-32 flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
  >
    <div className="flex items-start justify-between gap-3">
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors ${action.accent}`}>
        {action.icon}
      </span>
      <span aria-hidden="true" className="text-xl text-slate-300 transition group-hover:translate-x-1 group-hover:text-primary">→</span>
    </div>
    <div className="mt-4">
      <h3 className="font-semibold text-slate-950">{action.title}</h3>
      <p className="mt-1 text-sm leading-5 text-slate-500">{action.description}</p>
    </div>
  </Link>
);

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const [expiringContracts, setExpiringContracts] = useState(0);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [activeContractsResponse, recentPaymentsResponse, recentContractsResponse] = await Promise.all([
          api.get('/contratos?estado=ACTIVO'),
          api.get('/pagos?limit=5'),
          api.get('/contratos?limit=5'),
        ]);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const inThirtyDays = new Date(today);
        inThirtyDays.setDate(inThirtyDays.getDate() + 30);
        const activeContracts = getItems(activeContractsResponse);
        setExpiringContracts(activeContracts.filter((contract: any) => {
          if (!contract.fecha_fin) return false;
          const endDate = parseLocalDateKey(contract.fecha_fin);
          return endDate !== null && endDate >= today && endDate <= inThirtyDays;
        }).length);

        const activity: RecentActivity[] = [];
        getItems(recentPaymentsResponse).slice(0, 3).forEach((payment: any) => {
          activity.push({
            type: 'payment',
            description: `Pago ${payment.estado} · ${payment.tipo} · ${payment.moneda} ${Number(payment.monto).toLocaleString('es-UY')}`,
            date: payment.fecha_pago || payment.fecha_limite,
            id: payment.id,
          });
        });
        getItems(recentContractsResponse).slice(0, 2).forEach((contract: any) => {
          activity.push({
            type: 'contract',
            description: `Contrato ${contract.numero_contrato} · ${contract.estado}`,
            date: contract.fecha_inicio,
            id: contract.id,
          });
        });
        activity.sort((a, b) => (parseDateValue(b.date)?.getTime() ?? 0) - (parseDateValue(a.date)?.getTime() ?? 0));
        setRecentActivity(activity.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    void loadDashboard();
  }, []);

  const availableActions = QUICK_ACTIONS.filter((action) => (
    !action.permission || hasPermission(action.permission[0], action.permission[1])
  ));

  return (
    <MainLayout>
      <div className="mx-auto max-w-7xl space-y-7 p-4 sm:p-6">
        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-slate-800 to-slate-700 px-6 py-7 text-white shadow-nav sm:px-8 sm:py-9">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-300">Panel de gestión</p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                Bienvenido{user ? `, ${user.first_name}` : ''}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Acceda rápidamente a las operaciones diarias y mantenga el seguimiento de la inmobiliaria.
              </p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-slate-100 backdrop-blur-sm">
              {new Date().toLocaleDateString('es-UY', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>
        </section>

        <section aria-labelledby="quick-actions-heading">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <h2 id="quick-actions-heading" className="text-xl font-bold text-slate-950">Acciones Rápidas</h2>
              <p className="mt-1 text-sm text-slate-500">Atajos para las tareas más frecuentes</p>
            </div>
          </div>
          {availableActions.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {availableActions.map((action) => <QuickActionCard key={action.path} action={action} />)}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
              No hay acciones disponibles para su perfil.
            </div>
          )}
        </section>

        <section className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(280px,0.8fr)_minmax(0,1.7fr)]">
          <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">Próximos 30 días</p>
                <h2 className="mt-2 text-xl font-bold text-slate-950">Contratos por Vencer</h2>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <ActionIcon><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></ActionIcon>
              </span>
            </div>
            <p className="mt-6 text-4xl font-bold text-amber-800">{loading ? '—' : expiringContracts}</p>
            <p className="mt-1 text-sm text-slate-600">
              {expiringContracts === 1 ? 'contrato requiere seguimiento' : 'contratos requieren seguimiento'}
            </p>
            <Link to="/contracts" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-amber-800 hover:underline">
              Ver contratos <span aria-hidden="true">→</span>
            </Link>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">Actividad Reciente</h2>
                <p className="mt-1 text-sm text-slate-500">Últimos movimientos registrados</p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-primary">
                <ActionIcon><path d="M4 12h4l2-6 4 12 2-6h4" /></ActionIcon>
              </span>
            </div>
            {loading ? (
              <div className="py-8 text-sm text-slate-400">Cargando actividad…</div>
            ) : recentActivity.length === 0 ? (
              <div className="rounded-xl bg-slate-50 py-8 text-center text-sm text-slate-500">No hay actividad reciente</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentActivity.map((activity) => (
                  <Link
                    key={`${activity.type}-${activity.id}`}
                    to={activity.type === 'payment' ? `/payments/${activity.id}` : `/contracts/${activity.id}`}
                    className="flex items-center gap-3 rounded-lg px-2 py-3 transition hover:bg-slate-50"
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${
                      activity.type === 'payment' ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'
                    }`}>
                      {activity.type === 'payment' ? '$' : 'C'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">{activity.description}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {formatLocalDate(activity.date, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <span aria-hidden="true" className="text-slate-300">→</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
